import { EntityType } from 'features/config/Entities';
import {
    BaseEntityCommunication,
    CorrelatedEntityCommunication,
    EntityCommunicationActivity,
    EntityCommunicationActivityBreakdown,
    EntityCommunicationMonthlyAggregate,
} from 'interfaces/entity-communication.interface';

import {
    consultantLinkFormatter,
    DPOHLinkFormatter,
    institutionLinkFormatter,
    lobbyFirmLinkFormatter,
    organizationLinkFormatter,
    sectorLinkFormatter,
} from 'features/navigation/routers/internal_routing';
import moment, { Moment } from 'moment';

const entityTypeToEntityKey = (entityType: EntityType): keyof BaseEntityCommunication => {
    switch (entityType) {
        case EntityType.Institution:
            return 'institution_id';
        case EntityType.Sector:
            return 'sector_id';
        case EntityType.Organization:
            return 'organization';
        case EntityType.DPOH:
            return 'dpoh';
        case EntityType.Firm:
            return 'lobby_firm';
        default:
            throw new Error(`Unknown EntityType: ${entityType}`);
    }
};

const entityTypeToEntityName = (entityType: EntityType): keyof BaseEntityCommunication => {
    switch (entityType) {
        case EntityType.Institution:
            return 'institution';
        case EntityType.Sector:
            return 'sector';
        case EntityType.Organization:
            return 'organization';
        case EntityType.DPOH:
            return 'dpoh';
        case EntityType.Firm:
            return 'lobby_firm';
        default:
            throw new Error(`Unknown EntityType: ${entityType}`);
    }
};

export class CommunicationProcessor {
    private static filterRelevantCommunicationsByDate(
        communications: BaseEntityCommunication[],
        dateStart: Moment,
        dateEnd: Moment
    ) {
        return communications.filter((communication: BaseEntityCommunication) => {
            const commDate = moment(communication.date);
            const inPeriod = commDate.isSameOrAfter(dateStart) && commDate.isSameOrBefore(dateEnd);
            return inPeriod;
        });
    }

    private static filterRelevantCommunicationsByFields(
        communications: BaseEntityCommunication[],
        correlationTargetKeyFields: (keyof BaseEntityCommunication)[]
    ) {
        return communications.filter((communication: BaseEntityCommunication) => {
            return correlationTargetKeyFields.every(
                (key) =>
                    communication[key] && communication[key] !== '' && communication[key] !== 'null'
            );
        });
    }

    private static filterRelevantCommunicationsByFieldValues(
        communications: BaseEntityCommunication[],
        correlationTargetKeyField: keyof BaseEntityCommunication,
        correlationTargetKeyFieldValue: string
    ) {
        return communications.filter((communication: BaseEntityCommunication) => {
            return communication[correlationTargetKeyField] === correlationTargetKeyFieldValue;
        });
    }

    private static filterRelevantCommunicationsById(communications: BaseEntityCommunication[]) {
        const seenIds = new Set<number>();
        return communications.filter((communication: BaseEntityCommunication) => {
            if (seenIds.has(communication.id)) {
                return false;
            }
            seenIds.add(communication.id);
            return true;
        });
    }

    private static filterCommunicationsByUniqueComlogId(communications: BaseEntityCommunication[]) {
        const uniqueComlogIds = new Set();
        return communications.filter((communication: BaseEntityCommunication) => {
            if (uniqueComlogIds.has(communication.id)) {
                return false;
            }
            uniqueComlogIds.add(communication.id);
            return true;
        });
    }

    public static mapCommunicationTwoWayCorrelations(
        date: string,
        communications: BaseEntityCommunication[],
        correlationPeriodMonths: number = 12,
        correlationTargetTypeOne: EntityType,
        correlationTargetTypeTwo: EntityType
    ): CorrelatedEntityCommunication[] {
        const correlationTargetOneKey = entityTypeToEntityKey(correlationTargetTypeOne);
        const correlationTargetTwoKey = entityTypeToEntityKey(correlationTargetTypeTwo);

        let relevantCommunications: BaseEntityCommunication[] =
            this.filterRelevantCommunicationsByDate(
                communications,
                moment(date).subtract(correlationPeriodMonths, 'months'),
                moment(date)
            );

        relevantCommunications = this.filterRelevantCommunicationsByFields(communications, [
            correlationTargetOneKey,
            correlationTargetTwoKey,
        ]);

        if (
            correlationTargetTypeOne !== EntityType.DPOH &&
            correlationTargetTypeTwo !== EntityType.DPOH
        ) {
            relevantCommunications = this.filterRelevantCommunicationsById(communications);
        }

        let correlationMap: Map<string, BaseEntityCommunication[]> = new Map();

        relevantCommunications.forEach((communication: BaseEntityCommunication) => {
            const targetOneValue = String(communication[correlationTargetOneKey]);
            const targetTwoValue = String(communication[correlationTargetTwoKey]);

            if (!correlationMap.has(targetOneValue + targetTwoValue)) {
                correlationMap.set(targetOneValue + targetTwoValue, []);
            }

            correlationMap.get(targetOneValue + targetTwoValue)?.push(communication);
        });

        return communications.map((communication: BaseEntityCommunication) => {
            const targetOneValue = String(communication[correlationTargetOneKey]);
            const targetTwoValue = String(communication[correlationTargetTwoKey]);
            const communicationsForKey = correlationMap.get(targetOneValue + targetTwoValue) || [];
            return {
                ...communication,
                correlation: communicationsForKey.length,
            };
        });
    }

    public static mapCommunicationActivity(
        date: string,
        targetEntityType: EntityType,
        communications: BaseEntityCommunication[],
        correlationPeriodMonths: number = 6
    ): EntityCommunicationActivity[] {
        const correlationTargetKey = entityTypeToEntityKey(targetEntityType);

        let relevantCommunications: BaseEntityCommunication[] =
            this.filterRelevantCommunicationsByDate(
                communications,
                moment(date).subtract(correlationPeriodMonths, 'months'),
                moment(date)
            );

        relevantCommunications = this.filterRelevantCommunicationsByFields(relevantCommunications, [
            correlationTargetKey,
        ]);

        if (targetEntityType !== EntityType.DPOH) {
            relevantCommunications =
                this.filterCommunicationsByUniqueComlogId(relevantCommunications);
        }

        let correlationMap: Map<string, BaseEntityCommunication[]> = new Map();
        let entityKeyNameMap: Map<string, string> = new Map();

        relevantCommunications.forEach((communication: BaseEntityCommunication) => {
            const targetKeyValue = String(communication[correlationTargetKey]);

            if (!correlationMap.has(targetKeyValue)) {
                correlationMap.set(targetKeyValue, []);
            }

            if (!entityKeyNameMap.has(targetKeyValue)) {
                entityKeyNameMap.set(
                    targetKeyValue,
                    String(communication[entityTypeToEntityName(targetEntityType)])
                );
            }

            if (correlationMap.has(targetKeyValue)) {
                correlationMap.get(targetKeyValue)?.push(communication);
            } else {
                correlationMap.set(targetKeyValue, []);
            }
        });

        let results: EntityCommunicationActivity[] = [];

        Array.from(correlationMap.keys()).forEach((key: string) => {
            let communicationsForKey = correlationMap.get(key) || [];
            communicationsForKey.sort(
                (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
            );

            const periodCount = communicationsForKey.length;
            const periodPercent = Math.floor(
                (periodCount / relevantCommunications.length) * 100 || 0
            );

            let sectorId = undefined;
            let sector = undefined;
            if (
                targetEntityType === EntityType.Organization ||
                targetEntityType === EntityType.Sector
            ) {
                sectorId = communicationsForKey.find((c) => c.sector_id)?.sector_id || '';
                sector = communicationsForKey.find((c) => c.sector)?.sector || '';
            }

            let institutionId = undefined;
            let institution = undefined;
            let title = undefined;
            if (targetEntityType === EntityType.DPOH) {
                institutionId =
                    communicationsForKey.find((c) => c.institution_id)?.institution_id || '';
                institution = communicationsForKey.find((c) => c.institution)?.institution || '';
                title = communicationsForKey.find((c) => c.title)?.title || '';
            }

            results.push({
                entity_id: key,
                entity_name: entityKeyNameMap.get(key) || key,
                sectorId: sectorId,
                sector: sector,
                institutionId: institutionId,
                institution: institution,
                title: title,
                periodCount: periodCount,
                periodPercent: periodPercent,
            });
        });

        return results;
    }

    public static getTopCommunicatingEntities(
        date: string,
        communications: BaseEntityCommunication[],
        targetEntityType: EntityType,
        relevantPeriodMonths: number = 12,
        N: number = 3
    ): EntityCommunicationActivityBreakdown[] {
        const communicationCorrelations: EntityCommunicationActivity[] =
            this.mapCommunicationActivity(
                date,
                targetEntityType,
                communications,
                relevantPeriodMonths
            );

        const topNEntities = communicationCorrelations
            .sort((a, b) => b.periodCount - a.periodCount)
            .slice(0, N);

        return topNEntities.map((entity: EntityCommunicationActivity) => {
            return {
                ...entity,
                communicationBreakdown: this.getCommunicationMonthlyAggregates(
                    date,
                    communications,
                    relevantPeriodMonths,
                    targetEntityType,
                    entity.entity_id
                ),
            };
        });
    }

    public static getCommunicationMonthlyAggregates(
        date: string,
        communications: BaseEntityCommunication[],
        aggregatePeriodMonths: number = 12,
        targetEntityType: EntityType,
        correlationTargetKeyValue?: string
    ): EntityCommunicationMonthlyAggregate[] {
        const correlationTargetKey = entityTypeToEntityKey(targetEntityType);

        let relevantCommunications: BaseEntityCommunication[] =
            this.filterRelevantCommunicationsByDate(
                communications,
                moment(date).subtract(aggregatePeriodMonths, 'months'),
                moment(date)
            );

        relevantCommunications = this.filterRelevantCommunicationsByFields(relevantCommunications, [
            correlationTargetKey,
        ]);

        if (correlationTargetKeyValue) {
            relevantCommunications = this.filterRelevantCommunicationsByFieldValues(
                communications,
                correlationTargetKey,
                correlationTargetKeyValue
            );
        }

        relevantCommunications = this.filterCommunicationsByUniqueComlogId(relevantCommunications);

        const monthlyAggregates: EntityCommunicationMonthlyAggregate[] = [];
        for (let i = 0; i < aggregatePeriodMonths; i++) {
            const monthStart = moment(date).subtract(i, 'months').startOf('month');
            const monthEnd = moment(date).subtract(i, 'months').endOf('month');
            const count = relevantCommunications.filter((communication) => {
                const commDate = moment(communication.date);
                return commDate.isSameOrAfter(monthStart) && commDate.isSameOrBefore(monthEnd);
            }).length;

            monthlyAggregates.push({
                yr: +monthStart.format('YYYY'),
                mn: +monthStart.format('MM'),
                count: count,
            });
        }

        return monthlyAggregates;
    }

    public static formatEntityCommunicationLinks(
        communications: BaseEntityCommunication[]
    ): BaseEntityCommunication[] {
        return communications.map((entry: BaseEntityCommunication) => {
            return {
                ...entry,
                organization:
                    entry.sector_id && entry.sector && entry.organization
                        ? organizationLinkFormatter(
                              String(entry.sector_id),
                              entry.sector,
                              entry.organization
                          )
                        : entry.organization
                        ? entry.organization
                        : '',
                sector:
                    entry.sector_id && entry.sector
                        ? sectorLinkFormatter(String(entry.sector_id), entry.sector)
                        : entry.sector
                        ? entry.sector
                        : '',
                dpoh:
                    entry.institution_id && entry.institution && entry.dpoh
                        ? DPOHLinkFormatter(+entry.institution_id, entry.institution, entry.dpoh)
                        : entry.dpoh
                        ? entry.dpoh
                        : '',
                institution:
                    entry.institution_id && entry.institution
                        ? institutionLinkFormatter(+entry.institution_id, entry.institution)
                        : entry.institution
                        ? entry.institution
                        : '',
                lobby_firm:
                    entry.lobby_firm && entry.lobby_firm !== 'In-House'
                        ? lobbyFirmLinkFormatter(entry.lobby_firm)
                        : 'In-House',
                consultant:
                    entry.consultant && entry.consultant === 'In-House'
                        ? 'In-House'
                        : entry.lobby_firm && entry.lobby_firm === 'In-House' && entry.consultant
                        ? entry.consultant
                        : entry.lobby_firm && entry.consultant
                        ? consultantLinkFormatter(entry.lobby_firm, entry.consultant)
                        : 'In-House',
            };
        });
    }

    static filterCommunicationsByMonths(
        communications: BaseEntityCommunication[],
        dateEnd: string,
        monthPeriod: number = 6
    ): any[] {
        return communications.filter((communication: BaseEntityCommunication) => {
            const commDate = moment(communication.date);
            const startDate = moment(dateEnd).subtract(monthPeriod, 'months');
            const endDate = moment(dateEnd);
            return commDate.isSameOrAfter(startDate) && commDate.isSameOrBefore(endDate);
        });
    }
}
